import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Base64 } from 'js-base64';
import { forEach } from 'lodash-es';
import { Subject } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { CaptureQueueService, CaptureQueueType, PagedCaptureQueueResults } from '../../../core/services/capture-queue.service';
import { CaptureQueueCursorType } from '../../../core/enums/capture-queue-cursor-type.enum';
import { Cursor } from '../../../core/models/paged-results/cursor-based-paging.model';
import { CaptureQueueResult } from '../../../core/models/capture-lists/capture-queue-result.model';
import { WebsocketChannelHandle } from '../../../core/models/websocket-channel-handle.model';
import { NavigationService, PageChangeEvent } from '../../../core/services/navigation.service';
import { UserTimingService } from '../../../core/services/user-timing.service';
import { WebsocketService } from '../../../core/services/websocket.service';
import { UserPresenceService } from 'app/core/services/user-presence.service';
import { DecimalPipe } from '@angular/common';
import { CaptureQueueSettingsComponent } from '../../components/capture-queue-settings/capture-queue-settings.component';
import { CaptureQueueResultsComponent } from '../../components/capture-queue-results/capture-queue-results.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
    selector: 'app-capture-admin-capture-queue',
    templateUrl: './capture-admin-capture-queue.component.html',
    styleUrls: ['./capture-admin-capture-queue.component.scss'],
    imports: [CaptureQueueSettingsComponent, CaptureQueueResultsComponent, MatProgressSpinner, DecimalPipe]
})
export class CaptureAdminCaptureQueueComponent implements OnInit, OnDestroy {
  private captureQueueService = inject(CaptureQueueService);
  private navigationService = inject(NavigationService);
  private userPresenceService = inject(UserPresenceService);
  private userTimingService = inject(UserTimingService);
  private websocketService = inject(WebsocketService);

  getResults$: Subject<void> = new Subject();
  pageLoaded = false;
  loadingResults = false;
  hasMoreResults = false;
  limit = 10;
  advancementQueueCount: number = null;
  advancementQueueCountChannelHandle: WebsocketChannelHandle;
  captureQueueResults: CaptureQueueResult[] = [];
  cursor: Cursor<CaptureQueueCursorType>;
  error: string;

  constructor() {
    this.navigationService.pageChanged.subscribe((pageChangeEvent: PageChangeEvent) => {
      if (pageChangeEvent.navigatedBack && this.pageLoaded) {
        this.initCursorFromParams();
        this.getResults$.next();
      }
    });
  }

  ngOnInit() {
    this.captureQueueService.queueType = CaptureQueueType.work;
    this.configureGetResults();
    this.initCursorFromParams();
    this.subscribeToAdvancementQueueCount();
    this.pageLoaded = true;
  }

  onPerformSearch() {
    this.startTimeTracking();
    this.cursor = null;
    this.hasMoreResults = false;
    this.updateQueryParams();
    this.getResults$.next();
  }

  onPerformReset() {
    this.captureQueueResults = [];
  }

  onNextClick($event) {
    $event.preventDefault();
    this.updateQueryParams();
    this.getResults$.next();
    window.scrollTo(0, 0);
  }

  ngOnDestroy() {
    this.userTimingService.stop();
    this.advancementQueueCountChannelHandle.unsubscribe();
    this.userPresenceService.unsubscribeAll();
  }

  private updateQueryParams() {
    if (this.cursor) {
      const encodedCursor = encodeURIComponent(Base64.encode(JSON.stringify(this.cursor)));
      this.navigationService.updateQueryParams({ cursor: encodedCursor });
    } else {
      this.navigationService.updateQueryParams({}, true);
    }
  }

  private initCursorFromParams() {
    const queryParams = this.navigationService.queryParams;

    if ('cursor' in queryParams) {
      this.cursor = this.decodeCursor(queryParams);
    } else {
      this.cursor = null;
    }
  }

  private configureGetResults() {
    this.getResults$
      .pipe(
        tap(() => {
          this.loadingResults = true;
          this.captureQueueResults = [];
          this.userPresenceService.unsubscribeAll();
          this.error = null;
        }),
        switchMap(() => this.captureQueueService.getResults(this.cursor).pipe(
          catchError(error => {
            console.log(error);
            this.loadingResults = false;
            this.error = error.status === 429 ? "Too many requests. Please wait before trying again." : "An error occurred";
            return of(new PagedCaptureQueueResults());
          })
        ))
      )
      .subscribe(
        (results: PagedCaptureQueueResults) => {
          this.captureQueueResults = results.captureQueueResults;
          this.cursor = results.meta.cursor;
          this.loadingResults = false;
          this.hasMoreResults = !!results.meta.cursor;
          this.subscribeToUserPresence();
        });
  }

  private decodeCursor(queryParams) {
    try {
      const cursorParam = decodeURIComponent(queryParams['cursor']);
      return JSON.parse(Base64.decode(cursorParam));
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  private startTimeTracking() {
    const data: any = { page: 'capture_queue' };
    data.statuses = this.captureQueueService.getUserSettings().filters.statuses;

    this.userTimingService.track(data);
  }

  private subscribeToAdvancementQueueCount() {
    this.advancementQueueCountChannelHandle = this.websocketService.channelHandle(
      'CaptureAdvancementQueueCountChannel'
    );

    this.advancementQueueCountChannelHandle.messageReceived.subscribe(message => {
      this.advancementQueueCount = message.count;
    });
  }

  private subscribeToUserPresence() {
    forEach(this.captureQueueResults, (capture: CaptureQueueResult) => {
      this.userPresenceService.subscribe('capture', capture.id)
    })
  }
}
